import { FetchPolicy } from '@apollo/client';
import { BaseGraphqlService } from '@core/services/base';
import { DocumentTypes } from '@introspection/documentTypes';
import {
  ICreateOrUpdateLambdaInput,
  ICreateTimeTriggerInput,
  IDeleteLambdaInput,
  IFissionEnvironmentInputType,
  IMessageQueueTriggerInput,
  IMongoAtlasConnectorAllowedIpsInput,
  IMongoAtlasConnectPayload,
  IQuery,
  IRabbitMqInputQueue,
  IRabbitMqInstallPayload,
} from '@introspection/index';
import { LZWService } from '@rxdi/compressor';
import { Injectable } from '@rxdi/core';
import { deepCopy, importQuery } from '@rxdi/graphql-client';
import { map } from 'rxjs/operators';

@Injectable()
export class LambdaService extends BaseGraphqlService {
  createLambda(payload: ICreateOrUpdateLambdaInput, options?: { recreate?: boolean }) {
    return this.mutate({
      mutation: 'create-lambda.graphql',
      variables: {
        payload,
        recreate: options?.recreate,
      },
      update: (proxy, res) => {
        const query = importQuery<DocumentTypes>('list-project-lambdas.graphql');
        const variables = {
          projectId: payload.projectId,
        };
        const data: IQuery = deepCopy(
          proxy.readQuery({
            query,
            variables,
          })
        );
        if (data) {
          proxy.writeQuery({
            data: {
              listProjectLambdas: [...data.listProjectLambdas, res.data.createLambda],
            },
            query,
            variables,
          });
        }
      },
    }).pipe(map((res) => res.data.createLambda));
  }

  openLambda(lambdaId: string) {
    return this.mutate({
      mutation: 'open-lambda.graphql',
      errorPolicy: 'ignore',
      variables: {
        lambdaId,
      },
    }).pipe(map((res) => res.data.openLambda));
  }
  closeLambda(lambdaId: string) {
    return this.mutate({
      mutation: 'close-lambda.graphql',
      errorPolicy: 'ignore',
      variables: {
        lambdaId,
      },
    }).pipe(map((res) => res.data.closeLambda));
  }

  lambdaSubscription(lambdaId?: string) {
    return this.subscribe({
      query: 'lambda.subscription.graphql',
      variables: {
        lambdaId,
      },
    }).pipe(map((res) => res.data.lambdaSubscription));
  }

  deleteLambda(payload: IDeleteLambdaInput) {
    return this.mutate({
      mutation: 'delete-lambda.graphql',
      variables: {
        payload,
      },
      update: (proxy, { data: { deleteLambda } }) => {
        const query = importQuery('list-project-lambdas.graphql');
        const variables = {
          projectId: payload.projectId,
        };
        const data: IQuery = deepCopy(
          proxy.readQuery({
            query,
            variables,
          })
        );
        if (data) {
          proxy.writeQuery({
            data: {
              listProjectLambdas: data.listProjectLambdas.filter((lambda) => lambda.id !== deleteLambda.id),
            },
            query,
            variables,
          });
        }
      },
    }).pipe(map((res) => res.data.deleteLambda));
  }

  updateLambda(payload: ICreateOrUpdateLambdaInput) {
    delete payload.scaleOptions['__typename'];
    return this.mutate({
      mutation: 'update-lambda.graphql',
      variables: {
        payload,
      },
    }).pipe(map((res) => res.data.updateLambda));
  }

  createTimeTrigger(payload: ICreateTimeTriggerInput) {
    return this.mutate({
      mutation: 'create-lambda-time-trigger.graphql',
      variables: {
        payload,
      },
    }).pipe(map((res) => res.data.createLambdaTimeTrigger));
  }

  deleteTimeTrigger(lambdaId: string) {
    return this.mutate({
      mutation: 'delete-lambda-time-trigger.graphql',
      variables: {
        lambdaId,
      },
    }).pipe(map((res) => res.data.deleteLambdaTimeTrigger));
  }

  listProjectTimeTriggers(projectId: string) {
    return this.query({
      query: 'list-project-time-triggers.graphql',
      fetchPolicy: 'network-only',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.listProjectTimeTriggers));
  }

  listProjectLambdas(projectId: string, refresh?: boolean) {
    return this.watchQuery({
      query: 'list-project-lambdas.graphql',
      fetchPolicy: refresh ? 'network-only' : 'cache-first',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.listProjectLambdas));
  }

  getLambda(lambdaId: string, refresh?: boolean) {
    return this.query({
      query: 'get-lambda.graphql',
      fetchPolicy: refresh ? 'network-only' : 'cache-first',
      errorPolicy: 'ignore',
      variables: {
        lambdaId,
      },
    }).pipe(
      map((res) => ({
        getLambdaEditors: res.data.getLambdaEditors,
        getLambda: res.data.getLambda,
      }))
    );
  }

  getLambdaLogs(lambdaId: string) {
    return this.query({
      query: 'get-lambda-logs.graphql',
      fetchPolicy: 'network-only',
      variables: {
        lambdaId,
      },
    }).pipe(
      map((res) => res.data.getLambdaLogs),
      map((logs) => {
        return {
          ...logs,
          data: LZWService.decompress<string>(logs.data),
        };
      })
    );
  }

  getLambdaBuilderLogs(lambdaId: string) {
    return this.query({
      query: 'get-builder-logs.graphql',
      fetchPolicy: 'network-only',
      variables: {
        lambdaId,
      },
    }).pipe(
      map((res) => res.data.getLambdaBuilderLogs),
      map((logs) => {
        return {
          ...logs,
          data: LZWService.decompress<string>(logs.data),
        };
      })
    );
  }

  deleteEnvironment(id: string, projectId: string, force: boolean) {
    return this.mutate({
      mutation: 'delete-environment.graphql',
      variables: {
        id,
        projectId,
        force,
      },
      update(proxy, { data: { deleteEnvironment } }) {
        const query = importQuery<DocumentTypes>('list-project-environments.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
          variables: { projectId },
        });

        proxy.writeQuery({
          query,
          variables: {
            projectId,
          },
          data: {
            listEnvironmentsByProjectId: data.listEnvironmentsByProjectId.filter(
              (env) => env.id !== deleteEnvironment.id
            ),
          },
        });
      },
    }).pipe(map((res) => res.data.deleteEnvironment));
  }

  deleteAllEnvironments(projectId: string) {
    return this.mutate({
      mutation: 'delete-all-environment.graphql',
      variables: {
        projectId,
      },
      update(proxy) {
        const query = importQuery<DocumentTypes>('list-project-environments.graphql');
        proxy.writeQuery({
          query,
          variables: {
            projectId,
          },
          data: {
            listEnvironmentsByProjectId: [],
          },
        });
      },
    }).pipe(map((res) => res.data.deleteEnvironment));
  }

  createEnvironment(projectId: string, payload: IFissionEnvironmentInputType) {
    return this.mutate({
      mutation: 'create-environment.graphql',
      variables: {
        projectId,
        payload,
      },
      update(proxy, { data: { createEnvironment } }) {
        const query = importQuery<DocumentTypes>('list-project-environments.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
          variables: { projectId },
        });

        proxy.writeQuery({
          query,
          variables: {
            projectId,
          },
          data: {
            listEnvironmentsByProjectId: [...data.listEnvironmentsByProjectId, createEnvironment],
          },
        });
      },
    }).pipe(map((res) => res.data.createEnvironment));
  }

  updateEnvironment(id: string, projectId: string, payload: IFissionEnvironmentInputType) {
    return this.mutate({
      mutation: 'update-environment.graphql',
      variables: {
        id,
        projectId,
        payload,
      },
    }).pipe(map((res) => res.data.updateEnvironment));
  }

  listEnvironmentsByProjectId(projectId: string) {
    return this.query({
      query: 'list-project-environments.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.listEnvironmentsByProjectId));
  }

  installRabbitMq(projectId: string, payload: IRabbitMqInstallPayload) {
    return this.mutate({
      mutation: 'install-rabbitmq.graphql',
      variables: {
        projectId,
        payload,
      },
      update: (proxy, { data: { installRabbitMq } }) => {
        const query = importQuery<DocumentTypes>('get-project.query.graphql');
        const rabbitMqListQuery = importQuery<DocumentTypes>('list-rabbitmq-instances.graphql');

        const rabbitMq = proxy.readQuery<IQuery>({
          query: rabbitMqListQuery,
          variables: { projectId },
        });

        const data = proxy.readQuery<IQuery>({
          query,
          variables: { id: projectId },
        });

        proxy.writeQuery({
          query,
          variables: { id: projectId },
          data: {
            ...data,
            getProject: {
              ...data.getProject,
              rabbitMqCreated: true,
            },
          },
        });
        proxy.writeQuery({
          query: rabbitMqListQuery,
          variables: { projectId },
          data: {
            ...data,
            listRabbitMqInstances: [...rabbitMq.listRabbitMqInstances, installRabbitMq],
          },
        });
      },
    }).pipe(map((res) => res.data.installRabbitMq));
  }

  uninstallRabbitMq(id: string, projectId: string) {
    return this.mutate({
      mutation: 'remove-rabbitmq.graphql',
      variables: {
        id,
        projectId,
      },
      update: (proxy, { data: { uninstallRabbitMq } }) => {
        const query = importQuery<DocumentTypes>('get-project.query.graphql');
        const rabbitMqListQuery = importQuery<DocumentTypes>('list-rabbitmq-instances.graphql');

        const rabbitMq = proxy.readQuery<IQuery>({
          query: rabbitMqListQuery,
          variables: { projectId },
        });

        const data = proxy.readQuery<IQuery>({
          query,
          variables: { id: projectId },
        });
        proxy.writeQuery({
          query,
          variables: { id: projectId },
          data: {
            ...data,
            getProject: {
              ...data.getProject,
              rabbitMqCreated: false,
            },
          },
        });

        proxy.writeQuery({
          query: rabbitMqListQuery,
          variables: { projectId },
          data: {
            ...data,
            listRabbitMqInstances: rabbitMq.listRabbitMqInstances.filter((v) => v.id !== uninstallRabbitMq.id),
          },
        });
      },
    }).pipe(map((res) => res.data.uninstallRabbitMq));
  }

  declareQueue(id: string, projectId: string, payload: IRabbitMqInputQueue) {
    return this.mutate({
      mutation: 'declare-queue.graphql',
      variables: {
        id,
        projectId,
        payload,
      },
      update: (proxy, { data: { declareQueue } }) => {
        const query = importQuery<DocumentTypes>('list-rabbitmq-queues.graphql');
        const queryInstances = importQuery<DocumentTypes>('list-rabbitmq-instances.graphql');
        const data = proxy.readQuery<IQuery>({ query, variables: { id, projectId } });
        const state = proxy.readQuery<IQuery>({ query: queryInstances, variables: { id, projectId } });

        if (state?.listRabbitMqInstances) {
          const currentRabbitMqInstance = state?.listRabbitMqInstances?.find((i) => i.id === declareQueue.rabbitMqId);
          proxy.writeQuery({
            query: queryInstances,
            variables: { id, projectId },
            data: {
              listRabbitMqInstances: [
                ...state.listRabbitMqInstances?.filter((v) => v.id !== declareQueue.rabbitMqId),
                {
                  ...currentRabbitMqInstance,
                  queues: [...currentRabbitMqInstance.queues, declareQueue],
                },
              ],
            },
          });
        }

        if (data?.listQueuesPerProject) {
          proxy.writeQuery({
            query,
            variables: { id, projectId },
            data: {
              listQueuesPerProject: [...data.listQueuesPerProject, declareQueue],
            },
          });
        }
      },
    }).pipe(map((res) => res.data.declareQueue));
  }

  deleteQueue(id: string, projectId: string, queueName: string) {
    return this.mutate({
      mutation: 'delete-queue.graphql',
      variables: {
        id,
        projectId,
        queueName,
      },
      update: (proxy, { data: { deleteQueue } }) => {
        const query = importQuery<DocumentTypes>('list-rabbitmq-queues.graphql');
        const data = proxy.readQuery<IQuery>({ query, variables: { id, projectId } });
        proxy.writeQuery({
          query,
          variables: { id, projectId },
          data: {
            listQueuesPerProject: data?.listQueuesPerProject?.filter((v) => v.name !== deleteQueue.name) ?? [],
          },
        });
      },
    }).pipe(map((res) => res.data.declareQueue));
  }

  findRabbitMqInstance(id: string, projectId: string) {
    return this.query({
      query: 'find-rabbitmq-instance.graphql',
      variables: {
        id,
        projectId,
      },
    }).pipe(map((res) => res.data.findRabbitMqInstance));
  }

  listRabbitMqInstances(projectId: string) {
    return this.watchQuery({
      query: 'list-rabbitmq-instances.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.listRabbitMqInstances));
  }

  listQueuesPerProject(projectId: string, id?: string) {
    return this.query({
      query: 'list-rabbitmq-queues.graphql',
      variables: {
        id,
        projectId,
      },
    }).pipe(map((res) => res.data.listQueuesPerProject));
  }

  listMessageQueueTriggers(projectId: string) {
    return this.query({
      query: 'list-message-queue-triggers.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.listMessageQueueTriggers));
  }

  createMessageQueueTrigger(projectId: string, payload: IMessageQueueTriggerInput) {
    return this.mutate({
      mutation: 'create-message-queue-trigger.graphql',
      variables: {
        projectId,
        payload,
      },
    }).pipe(map((res) => res.data.createMessageQueueTrigger));
  }

  updateMessageQueueTrigger(projectId: string, payload: IMessageQueueTriggerInput) {
    return this.mutate({
      mutation: 'update-message-queue-trigger.graphql',
      variables: {
        projectId,
        payload,
      },
    }).pipe(map((res) => res.data.updateMessageQueueTrigger));
  }

  deleteMessageQueueTrigger(projectId: string, name: string) {
    return this.mutate({
      mutation: 'delete-message-queue-trigger.graphql',
      variables: {
        projectId,
        name,
      },
    }).pipe(map((res) => res.data.deleteMessageQueueTrigger));
  }

  getLimitRange(projectId: string) {
    return this.query({
      query: 'get-limit-range.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.getLimitRange));
  }

  getResourceQuota(projectId: string) {
    return this.query({
      query: 'get-resource-quota.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.getResourceQuota));
  }

  getPodsCapacityPerNamespace(projectId: string) {
    return this.query({
      query: 'get-pod-capacity.graphql',
      variables: {
        projectId,
      },
    }).pipe(map((res) => res.data.getPodsCapacityPerNamespace));
  }

  getAllMetrics(projectId: string, fetchPolicy?: FetchPolicy) {
    return this.query({
      query: 'metrics-all.graphql',
      errorPolicy: 'ignore',
      fetchPolicy,
      variables: {
        projectId,
      },
    }).pipe(
      map(({ data }) => ({
        podsCapacity: data.getPodsCapacityPerNamespace,
        resourceQuota: data.getResourceQuota,
        limitRange: data.getLimitRange,
        environments: data.listEnvironmentsByProjectId,
        projectStatistics: data.getProjectStatistics,
        listProjectLambdas: data.listProjectLambdas,
        listProjectTimeTriggers: data.listProjectTimeTriggers,
        listQueuesPerProject: data.listQueuesPerProject,
        getPersistentVolumes: data.getPersistentVolumes,
        getFunctionStatistics: data.getFunctionStatistics,
        listMessageQueueTriggers: data.listMessageQueueTriggers,
        getAccumulatedPricing: data.getAccumulatedPricing,
      }))
    );
  }

  getAccumulatedPricingPod(projectId: string, fetchPolicy?: FetchPolicy) {
    return this.query({
      query: 'pricing-per-pod.graphql',
      fetchPolicy,
      variables: {
        projectId,
      },
    }).pipe(map(({ data }) => data.getAccumulatedPricing));
  }

  connectAtlasMongo(projectId: string, payload: IMongoAtlasConnectPayload) {
    return this.mutate({
      mutation: 'connect-mongo-atlas.graphql',
      variables: {
        projectId,
        payload,
      },
    }).pipe(map(({ data }) => data.connectAtlasMongo));
  }

  updateAtlasMongo(id: string, projectId: string, allowedIps: IMongoAtlasConnectorAllowedIpsInput[]) {
    return this.mutate({
      mutation: 'update-mongo-atlas.graphql',
      variables: {
        id,
        projectId,
        allowedIps,
      },
    }).pipe(map(({ data }) => data.connectAtlasMongo));
  }

  disconnectAtlasMongo(id: string, projectId: string) {
    return this.mutate({
      mutation: 'disconnect-mongo-atlas.graphql',
      variables: {
        id,
        projectId,
      },
    }).pipe(map(({ data }) => data.disconnectAtlasMongo));
  }

  listAtlasConnectors(projectId: string) {
    return this.query({
      query: 'list-atlas-connectors.graphql',
      variables: {
        projectId,
      },
    }).pipe(
      map(({ data }) => ({
        listAtlasConnectors: data.listAtlasConnectors,
        form: data.getFormByName,
      }))
    );
  }
}
